// src/components/WatchlistItemSidebar.jsx
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { redirectToLogin } from "../utils/auth";

export default function WatchlistItemSidebar({
  open,
  itemId,
  jwtToken,
  onClose,
  size = "normal",
  detailUrl = "https://whiteintel.io/api/regular/app/v2/watchlist/get_watchlist_items_v2.php",
  saveUrl = "https://whiteintel.io/api/regular/app/v2/watchlist/get_watchlist_items_v2.php",
  backdropZ = 400,
  panelZ = 500,
  onStatusChange,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorDetail, setErrorDetail] = useState("");
  const [detail, setDetail] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const canCall = !!open && !!jwtToken && !!itemId;

  const slackReady = !!detail?.slack_active;
  const lookalikeReady = !!detail?.lookalike_allowed;
  const jiraReady = !!detail?.jira_active;
  const detectLookalike = !!detail?.include_lookalike;
  const entryType = !! detail?.entry_type;
  // Auto-uncheck toggles if integration isn't configured
  useEffect(() => {
    if (!detail) return;
    const needSlackOff = !slackReady && !!detail.include_slack;
    const needJiraOff = !jiraReady && !!detail.include_jira;
    if (needSlackOff || needJiraOff) {
      setDetail((d) => ({
        ...d,
        include_slack: slackReady ? !!d.include_slack : false,
        include_jira: jiraReady ? !!d.include_jira : false,
      }));
    }
  }, [slackReady, jiraReady, detail]);

  // ---- fetch item detail
  useEffect(() => {
    if (!canCall) return;
    setLoading(true);
    setErrorDetail("");
    setSuccessMsg("");

    (async () => {
      try {
        const res = await fetch(detailUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify({ id: Number(itemId), metric: "detail" }),
        });
        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const item = Array.isArray(json) ? json[0] : json;
        setDetail(item ?? null);
      } catch (e) {
        setErrorDetail(e.message || "Failed to load watchlist item");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, itemId, jwtToken, detailUrl, canCall]);

  // Save handler
  const handleSave = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const res = await fetch(saveUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({
          id: itemId,
          metric: "update",
          include_usernames: detail.include_usernames ? 1 : 0,
          include_jira: detail.include_jira ? 1 : 0,
          include_slack: detail.include_slack ? 1 : 0, // FIXED
          consumer_alerts: detail.consumer_alerts ? 1 : 0,
          corporate_alerts: detail.corporate_alerts ? 1 : 0,
          notify_email: detail.notify_email || "",
          detect_look_alike: detail.include_lookalike || 0,
          status: detail.status,
        }),
      });
      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }
      if (!res.ok) throw new Error("Failed to save watchlist item");
      onStatusChange?.(itemId, detail.status);
      setSuccessMsg("Watchlist item updated successfully.");
    } catch (e) {
      console.error(e);
      setErrorDetail(e.message || "Failed to save watchlist item");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {open &&
        createPortal(
          <div
            role="presentation"
            onClick={onClose}
            aria-hidden="true"
            style={{ zIndex: backdropZ }}
            className="fixed inset-0 bg-black/60"
          />,
          document.body
        )}

      {open &&
        createPortal(
          <aside
            style={{ zIndex: panelZ }}
            className={`fixed right-0 top-0 h-full w-full ${
              size === "wide" ? "max-w-4xl" : "max-w-2xl"
            } bg-[#000414] border-l border-neutral-800 flex flex-col`}
            onClick={(e) => e.stopPropagation()} // prevent backdrop close on clicks inside
          >
            {/* Header */}
            <div className="flex items-start justify-between px-5 py-4 border-b border-neutral-800">
              <div>
                <h2 className="text-xl font-semibold">
                  Watchlist Item — {detail?.entry}
                </h2>
                <p className="text-sm text-neutral-400">{detail?.entry_type}</p>
              </div>
              <IconBtn title="Close" onClick={onClose}>
                <X className="h-4 w-4" />
              </IconBtn>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {loading ? (
                <Loader />
              ) : errorDetail ? (
                <div className="text-red-400">{errorDetail}</div>
              ) : detail ? (
                <form className="space-y-5" onSubmit={(e) => e.preventDefault()}>
                  <Field label="Entry Type">{detail.entry_type}</Field>
                  <Field label="Entry">{detail.entry}</Field>
                  <Field label="Added By">{detail.added_by}</Field>
                  <Field label="Added At">{fmtISO(detail.added_at)}</Field>

                    {detail.entry_type === "domain" && (
                    <CheckboxField
                    label={`Detect lookalike domains${lookalikeReady ? "" : " (Requires higher license.)"}`}
                    checked={!!detail.include_lookalike}
                    disabled={!lookalikeReady}
                    onChange={(v) =>
                      setDetail((d) => ({ ...d, include_lookalike: v }))
                    }
                    title={
                      slackReady
                        ? "Send events to Slack"
                        : "Configure Slack in Integrations to enable"
                    }
                  />
                  )}
                  <CheckboxField
                    label={`Push to Slack${slackReady ? "" : " (Not configured yet)"}`}
                    checked={!!detail.include_slack}
                    disabled={!slackReady}
                    onChange={(v) =>
                      setDetail((d) => ({ ...d, include_slack: v }))
                    }
                    title={
                      slackReady
                        ? "Send events to Slack"
                        : "Configure Slack in Integrations to enable"
                    }
                  />

{/* Hide Jira/Consumer/Corporate when entry_type is 'keyword' */}
{detail.entry_type !== "keyword" && (
  <>
    <CheckboxField
      label={`Push to Jira${jiraReady ? "" : " (Not configured yet)"}`}
      checked={!!detail.include_jira}
      disabled={!jiraReady}
      onChange={(v) => setDetail((d) => ({ ...d, include_jira: v }))}
      title={jiraReady ? "Send events to Jira" : "Configure Jira in Integrations to enable"}
    />

    <CheckboxField
      label="Consumer Alerts"
      checked={!!detail.consumer_alerts}
      onChange={(v) => setDetail((d) => ({ ...d, consumer_alerts: v }))}
    />

    <CheckboxField
      label="Corporate Alerts"
      checked={!!detail.corporate_alerts}
      onChange={(v) => setDetail((d) => ({ ...d, corporate_alerts: v }))}
    />
  </>
)}

                  <Field label="Notify Email">
                    <input
                      type="email"
                      value={detail.notify_email || ""}
                      onChange={(e) =>
                        setDetail((d) => ({
                          ...d,
                          notify_email: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-sky-500/40"
                    />
                  </Field>

                  <Field label="Status">
                    <select
                      value={detail.status}
                      onChange={(e) =>
                        setDetail((d) => ({ ...d, status: e.target.value }))
                      }
                      className="w-full rounded-lg border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-sky-500/40"
                    >
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </select>
                  </Field>
                </form>
              ) : (
                <div className="text-neutral-400">No data available</div>
              )}
            </div>

            {/* Sticky Footer */}
            <div className="sticky bottom-0 bg-[#000414] border-t border-neutral-800 p-4 flex flex-col gap-3">
              {successMsg && (
                <div className="flex items-center justify-between rounded-lg bg-green-600/20 text-green-300 px-4 py-2 text-sm border border-green-700">
                  <span>{successMsg}</span>
                  <button
                    onClick={() => setSuccessMsg("")}
                    className="ml-2 text-green-300 hover:text-green-100"
                    title="Dismiss"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-lg border border-neutral-700 text-neutral-300 hover:bg-neutral-800 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg border border-sky-600/40 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20 text-sm disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </div>
          </aside>,
          document.body
        )}
    </>
  );
}

/* ---------------- helpers ---------------- */

function IconBtn({ children, title, onClick, disabled }) {
  return (
    <button
      className={`p-2 rounded ${
        disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-white/5"
      }`}
      title={title}
      onClick={onClick}
      disabled={disabled}
      type="button"
    >
      {children}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-400">
        {label}
      </label>
      <div className="text-sm text-neutral-100">{children}</div>
    </div>
  );
}

function CheckboxField({ label, checked, onChange, disabled = false, title }) {
  return (
    <label
      className={`flex items-center gap-2 text-sm ${
        disabled ? "text-neutral-500" : "text-neutral-300"
      }`}
      title={title}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="h-4 w-4 rounded border-neutral-700 bg-neutral-900 text-sky-500 disabled:opacity-60 disabled:cursor-not-allowed"
      />
      {label}
    </label>
  );
}

function Loader() {
  return (
    <div className="p-5 space-y-5">
      <div className="h-5 w-36 bg-neutral-800/60 animate-pulse rounded" />
      <div className="space-y-3">
        <div className="h-4 w-64 bg-neutral-800/60 animate-pulse rounded" />
        <div className="h-4 w-48 bg-neutral-800/60 animate-pulse rounded" />
      </div>
    </div>
  );
}

function fmtISO(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toISOString().slice(0, 10);
}
